import { motion } from "framer-motion";
import Button from "../../../components/button/button";
import teens1 from "../../../assets/videos/teens1.mp4";
import teens2 from "../../../assets/videos/teens2.mp4";
import teens3 from "../../../assets/videos/teens3.mp4";

const StudentLedProject = () => {
  const projects = [
    {
      number: "01",
      title: "Ideas Become Real",
      category: "BUILD",
      description:
        "Students turn curiosity into working digital experiences through guided projects, experimentation and teamwork.",
      video: teens1,
      accent: "#59b5cd",
    },
    {
      number: "02",
      title: "Built By Young Minds",
      category: "CREATE",
      description:
        "From the first sketch to the final prototype, students learn how to transform their ideas into something people can actually use.",
      video: teens2,
      accent: "#ecb32c",
    },
    {
      number: "03",
      title: "Learning Through Doing",
      category: "DISCOVER",
      description:
        "Every project gives students an opportunity to collaborate, solve problems and discover what they are capable of.",
      video: teens3,
      accent: "#cf375d",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-[#07151c] py-28 sm:py-32">

      {/* =====================================================
          AMBIENT BACKGROUND
      ====================================================== */}

      <div className="absolute inset-0 pointer-events-none">

        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />

        {/* Cyan glow */}
        <motion.div
          className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#59b5cd]/20 blur-[130px]"
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Yellow glow */}
        <motion.div
          className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#ecb32c]/15 blur-[130px]"
          animate={{
            x: [0, -40, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

      </div>


      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-end mb-20">

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >

            <div className="flex items-center gap-3 mb-6">

              <span className="w-10 h-[2px] bg-[#ecb32c]" />

              <span className="text-[#ecb32c] uppercase tracking-[0.25em] text-xs font-bold">
                Student Showcase
              </span>

            </div>


            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[0.95]">

              They didn't
              <br />

              <span className="text-[#59b5cd]">
                just learn.
              </span>

              <br />

              <span className="relative inline-block">

                They built.

                <motion.span
                  initial={{ width: 0 }}
                  whileInView={{ width: "75%" }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.8,
                    delay: 0.4,
                  }}
                  className="absolute left-0 -bottom-2 h-2 bg-[#ecb32c] rounded-full"
                />

              </span>

            </h2>

          </motion.div>


          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl"
          >

            <p className="text-gray-300 text-lg sm:text-xl lg:text-2xl leading-relaxed">

              The most powerful part of our teen isn't what students
              <span className="text-white font-semibold">
                {" "}memorize.
              </span>

             

              It's what they become capable of
              <span className="text-[#59b5cd] font-semibold">
                {" "}creating.
              </span>

            </p>

          </motion.div>

        </div>


        {/* =====================================================
            FEATURED PROJECT
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative mb-16"
        >

          {/* floating label */}

          <div className="absolute -top-5 left-6 sm:left-10 z-20">

            <div className="bg-[#ecb32c] text-[#07151c] px-5 py-3 rounded-full font-black text-xs uppercase tracking-widest shadow-xl">
              Featured Student Work
            </div>

          </div>


          <div className="grid lg:grid-cols-[1.5fr_0.8fr] bg-white rounded-[2rem] overflow-hidden shadow-[0_30px_100px_rgba(0,0,0,0.25)]">

            {/* VIDEO */}

            <div className="relative bg-black min-h-[320px] lg:min-h-[520px]">

              <video
                src={teens1}
                controls
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* overlay */}

              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/40 via-transparent to-transparent" />


              {/* project number */}

              <div className="absolute bottom-6 left-6">

                <span className="text-white/60 text-xs uppercase tracking-widest font-bold">
                  Project 01
                </span>

              </div>

            </div>


            {/* STORY */}

            <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-between">

              <div>

                <p className="text-[#59b5cd] text-xs font-bold uppercase tracking-[0.25em] mb-5">
                  From idea → prototype
                </p>

                <h3 className="text-3xl sm:text-4xl font-black text-[#07151c] leading-tight mb-5">
                  Ideas deserve
                  <br />
                  to be built.
                </h3>

                <p className="text-gray-600 leading-relaxed">
                  Students aren't handed finished solutions.
                  They're given challenges, tools and the freedom to
                  experiment — then they build their own answers.
                </p>

              </div>


              <div className="mt-10">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-full bg-[#59b5cd]/10 flex items-center justify-center text-xl">
                    💡
                  </div>

                  <div>
                    <p className="font-black text-[#07151c]">
                      Curiosity → Creation
                    </p>

                    <p className="text-sm text-gray-500">
                      Project-based learning
                    </p>
                  </div>

                </div>

              </div>

            </div>

          </div>

        </motion.div>


        {/* =====================================================
            PROJECT GRID
        ====================================================== */}

        <div className="grid md:grid-cols-2 gap-6">

          {projects.slice(1).map((project, index) => (

            <motion.div
              key={project.number}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: index * 0.15,
              }}
              whileHover={{ y: -8 }}
              className="group bg-[#10232b] border border-white/10 rounded-[1.75rem] overflow-hidden"
            >

              {/* VIDEO */}

              <div className="relative aspect-video overflow-hidden bg-black">

                <video
                  src={project.video}
                  controls
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* category */}

                <div className="absolute top-4 left-4">

                  <span
                    className="px-4 py-2 rounded-full text-[10px] font-black tracking-widest text-[#07151c]"
                    style={{
                      backgroundColor: project.accent,
                    }}
                  >
                    {project.category}
                  </span>

                </div>

              </div>


              {/* CONTENT */}

              <div className="p-7 sm:p-8">

                <div className="flex justify-between items-start gap-4">

                  <div>

                    <span className="text-white/30 text-xs font-black tracking-widest">
                      {project.number}
                    </span>

                    <h3 className="text-2xl sm:text-3xl font-black text-white mt-2 mb-3">
                      {project.title}
                    </h3>

                  </div>


                  <motion.div
                    whileHover={{
                      rotate: 45,
                      scale: 1.1,
                    }}
                    className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white"
                  >
                    ↗
                  </motion.div>

                </div>


                <p className="text-gray-400 leading-relaxed text-sm">
                  {project.description}
                </p>

              </div>

            </motion.div>

          ))}

        </div>


        {/* =====================================================
            IMPACT STATEMENT
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative mt-20 rounded-[2rem] border border-white/10 bg-white/[0.03] backdrop-blur-sm p-8 sm:p-12 overflow-hidden"
        >

          {/* giant Z */}

          <div className="absolute -right-8 -top-20 text-[220px] font-black text-white/[0.025] select-none">
            Z
          </div>


          <div className="relative grid md:grid-cols-[1fr_auto] gap-10 items-center">

            <div>

              <p className="text-[#ecb32c] text-xs font-bold uppercase tracking-[0.25em] mb-4">
                The bigger picture
              </p>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight max-w-3xl">

                A project is more than
                <span className="text-[#59b5cd]">
                  {" "}an assignment.
                </span>

                <br />

                It's proof of what's possible.

              </h3>

            </div>


            <motion.div
              whileHover={{
                scale: 1.08,
                rotate: 5,
              }}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#ecb32c] flex items-center justify-center shadow-[0_0_50px_rgba(236,179,44,0.2)]"
            >

              <span className="text-4xl">
                🚀
              </span>

            </motion.div>

          </div>

        </motion.div>


        {/* =====================================================
            CTA
        ====================================================== */}

        <div className="text-center mt-16">

          <p className="text-gray-400 mb-6">
            Ready to help your child become a builder?
          </p>

          <a 
            href="https://wa.me/2348115371014?text=Hello Zulfah Team, I’m interested in the Tech4Teens programme and would love to help my teenager develop practical technology skills for the future. Please share more information about the programme, courses, schedule, fees, and enrolment process"
           target="_blank"
              rel="noreferrer"
            >
          <Button
            btnText="Start your Journey today"
            btnClassName="!bg-[#59b5cd] hover:!bg-[#ecb32c] !text-[#07151c] w-[50%] font-bold px-8 py-4 rounded-full shadow-lg hover:scale-105 transition-all duration-300"
          />
          </a>

        </div>

      </div>

    </section>
  );
};

export default StudentLedProject;